"use client";

/**
 * GoogleAnalytics — loads gtag.js (GA4) only after the user accepts optional
 * analytics in the cookie banner (GDPR: the app promises "with your consent
 * we may use optional analytics"). Reads the same consent store the banner
 * writes (localStorage 'bb-cookie-consent' + the live 'bb:cookie-consent'
 * event), so accepting the banner starts tracking without a reload, and
 * "Essential only" keeps GA fully unloaded — no cookies, no requests.
 *
 * SPA page views: GA4's enhanced measurement tracks history changes by
 * default, so App Router navigations are counted without extra wiring.
 */
import Script from "next/script";
import { useEffect, useState } from "react";

const GA_ID = "G-0S0FZEFK4D";

export function GoogleAnalytics() {
  // Starts false on server AND first client render (hydration-safe);
  // consent is read post-mount.
  const [consented, setConsented] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem("bb-cookie-consent") === "accepted") setConsented(true);
    } catch { /* restricted storage — treat as no consent */ }
    const onConsent = (e: Event) => {
      setConsented((e as CustomEvent).detail === "accepted");
    };
    window.addEventListener("bb:cookie-consent", onConsent);
    return () => window.removeEventListener("bb:cookie-consent", onConsent);
  }, []);

  if (!consented) return null;

  return (
    <>
      <Script src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`} strategy="afterInteractive" />
      <Script id="ga-gtag" strategy="afterInteractive">{`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${GA_ID}');
      `}</Script>
    </>
  );
}
